import React from 'react';
import clsx from 'clsx';
import { Icon } from '@common/components';
import {
  Colors,
  ColorsType,
  Duration,
  Easing,
  FontSize,
  FontWeight,
  LineHeight,
  Opacity,
  Radius,
  Spacing,
} from '@common/tokens';
import { getChipBorderColor, getReadableTextColor } from './_tag-service';

type TagProps = {
  children: React.ReactNode;
  /** Background color token. */
  color: ColorsType;
  /**
   * Text color token. When omitted, a readable color is derived from the
   * background's luminance (dark text on light backgrounds, white on dark).
   */
  textColor?: ColorsType;
  /**
   * Shape only (radius, border, weight) — never font-size or padding:
   * `pill` is the classic rounded tag; `square` is a square-cornered variant
   * (smaller radius, medium weight) for inline metric/delta readouts;
   * `chip` is a bordered regular-weight variant for removable filter chips.
   */
  kind?: 'pill' | 'square' | 'chip';
  /**
   * Scale only — the single owner of font-size and padding: `sm` drops the
   * vertical padding and tightens the horizontal one for dense spots (e.g.
   * calendar header counters); `md` is the regular 12px tag; `lg` bumps the
   * text to 14px (e.g. filter chips).
   */
  size?: 'sm' | 'md' | 'lg';
  /** Renders the tag as a button. The whole tag is the click target. */
  onClick?: () => void;
  /** Accessible name for clickable tags whose visible label isn't enough. */
  ariaLabel?: string;
  /** Shows the xMark icon on the right side (e.g. a removable filter chip). */
  showX?: boolean;
};

export const Tag = ({
  children,
  color,
  textColor = getReadableTextColor(color),
  kind = 'pill',
  size = 'md',
  onClick,
  ariaLabel,
  showX = false,
  // Not dead code despite the closed TagProps: antd overlay components
  // (Tooltip, Dropdown, Popover) inject handlers into their child via
  // cloneElement at runtime, invisible to the type system. Dropping the
  // spread silently breaks a Tag wrapped by any of them.
  ...props
}: TagProps) => {
  const className = clsx(
    'wk-tag',
    `wk-tag--${kind}`,
    `wk-tag--size-${size}`,
    onClick && 'wk-tag--clickable',
    showX && 'wk-tag--with-x'
  );
  const content = (
    <>
      {children}
      {showX && (
        <span className="wk-tag__x">
          <Icon icon="xMark" kind="light" size="sm" />
        </span>
      )}
    </>
  );

  return (
    <>
      {/* Two explicit elements (not a dynamic tag): styled-jsx only injects its
          scoping class into lowercase DOM elements, so a computed <Element>
          would silently lose every .wk-tag style. */}
      {onClick ? (
        <button
          type="button"
          className={className}
          onClick={onClick}
          aria-label={ariaLabel}
          {...props}
        >
          {content}
        </button>
      ) : (
        <span className={className} aria-label={ariaLabel} {...props}>
          {content}
        </span>
      )}
      <style jsx>{`
        .wk-tag {
          display: inline-block;
          white-space: nowrap;
          color: ${Colors[textColor]};
          background-color: ${Colors[color]};
          line-height: ${LineHeight.sm};
          border: 1px solid transparent;
        }
        /* Kinds own shape (radius, border, weight); sizes own font-size and
           padding. Keep the axes apart: a kind must never set font/padding. */
        .wk-tag--pill {
          border-radius: ${Radius.lg};
          font-weight: ${FontWeight.bold};
        }
        .wk-tag--square {
          border-radius: ${Radius.sm};
          font-weight: ${FontWeight.medium};
        }
        .wk-tag--chip {
          border-radius: ${Radius.xl};
          border-color: ${getChipBorderColor(color)};
          font-weight: ${FontWeight.regular};
        }
        .wk-tag--size-sm {
          font-size: ${FontSize.xs};
          padding: 0 6px;
        }
        .wk-tag--size-md {
          font-size: ${FontSize.xs};
          padding: ${Spacing.xxs}px ${Spacing.sm}px;
        }
        .wk-tag--size-lg {
          font-size: ${FontSize.sm};
          padding: ${Spacing.xxs}px ${Spacing.sm}px;
        }
        .wk-tag--with-x {
          display: inline-flex;
          align-items: center;
          gap: ${Spacing.xs}px;
        }
        .wk-tag--clickable {
          cursor: pointer;
          font-family: inherit;
          transition: opacity ${Duration.fast} ${Easing.easeInOut};
        }
        .wk-tag--clickable:hover {
          opacity: ${Opacity.high};
        }
        /* outline, not box-shadow: shadows aren't painted under
           forced-colors (Windows High Contrast), which would leave keyboard
           users with no focus indicator. */
        .wk-tag--clickable:focus-visible {
          outline: 2px solid ${Colors.elm};
          outline-offset: 2px;
        }
      `}</style>
    </>
  );
};
